import React, { useEffect } from 'react';
import TabContext from '@mui/lab/TabContext';
import { SxProps, Theme } from '@mui/material/styles';

export interface TabMenuDef {
  label: string;
  value: string;
  disabled?: boolean;
  hidden?: boolean;
}

interface TabMenuProps {
  value: string;
  onChange: ((value: string) => void) | ((event: React.SyntheticEvent, value: string) => void);
  tabs: TabMenuDef[];
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  rightElement?: React.ReactNode;
}

/**
 * Единые стили форм-элементов портала — инжектируются один раз в <head>.
 * Все секции через TabMenu автоматически получают согласованный вид.
 *
 * ПРАВИЛО: Если появляются новые типы элементов (кнопки, чекбоксы, поиск и т.д.) —
 * стиль по умолчанию прописывать здесь, а не дублировать на каждой странице.
 */
const SELECT_CSS = `
/* ── select ── */
.tm-scope select {
  height: 32px;
  padding: 0 26px 0 8px;
  background: var(--bg-input, var(--btn-user-form));
  color: var(--text-color);
  border: 1px solid var(--border-input);
  border-radius: 5px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6' fill='none' stroke='%239aaabf' stroke-width='1.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  transition: border-color .15s;
}
.tm-scope select:hover  { border-color: rgba(37,99,235,.35); }
.tm-scope select:focus  { border-color: rgba(37,99,235,.5); box-shadow: 0 0 0 2px rgba(37,99,235,.08); outline: none; }

/* ── text / search / number inputs ── */
.tm-scope input[type="text"],
.tm-scope input[type="search"],
.tm-scope input[type="number"],
.tm-scope input[type="email"],
.tm-scope input[type="password"] {
  height: 32px;
  padding: 0 10px;
  background: var(--bg-input, var(--btn-user-form));
  color: var(--text-color);
  border: 1px solid var(--border-input);
  border-radius: 5px;
  font-size: 13px;
  outline: none;
  transition: border-color .15s;
}
.tm-scope input[type="text"]:hover,
.tm-scope input[type="search"]:hover,
.tm-scope input[type="number"]:hover { border-color: rgba(37,99,235,.35); }
.tm-scope input[type="text"]:focus,
.tm-scope input[type="search"]:focus,
.tm-scope input[type="number"]:focus  { border-color: rgba(37,99,235,.5); box-shadow: 0 0 0 2px rgba(37,99,235,.08); }

/* ── date / time inputs ── */
.tm-scope input[type="date"],
.tm-scope input[type="time"] {
  height: 32px;
  padding: 0 8px;
  background: var(--bg-input, var(--btn-user-form));
  color: var(--text-color);
  border: 1px solid var(--border-input);
  border-radius: 5px;
  font-size: 13px;
  outline: none;
  transition: border-color .15s;
}
.tm-scope input[type="date"]:focus,
.tm-scope input[type="time"]:focus { border-color: rgba(37,99,235,.5); box-shadow: 0 0 0 2px rgba(37,99,235,.08); }

/* ── checkbox — исключаем MUI Switch/Checkbox внутренние инпуты ── */
.tm-scope input[type="checkbox"]:not(.MuiSwitch-input):not(.PrivateSwitchBase-input):not([class*="Mui"]) {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid var(--border-input);
  background: var(--bg-input, var(--btn-user-form));
  accent-color: #2563eb;
  cursor: pointer;
}

/* ── placeholder ── */
.tm-scope input::placeholder { color: var(--text-color-calendar-week); }

/* ── dt-search-wrap inside tm-scope — restore from style.css vars ── */
.tm-scope .dt-search-wrap input[type="text"],
.tm-scope .dt-search-wrap input[type="search"] {
  height: var(--search-height, 32px);
  padding: 0 10px 0 var(--search-pl, 30px);
  border-radius: var(--search-radius, 8px);
  font-size: var(--search-font, 12px);
  appearance: none;
  -webkit-appearance: none;
}
`;

let styleInjected = false;

function injectSelectStyles() {
  if (styleInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.id = 'tm-select-styles';
  el.textContent = SELECT_CSS;
  document.head.appendChild(el);
  styleInjected = true;
}

export default function TabMenu({ value, onChange, tabs, children, rightElement }: TabMenuProps) {
  useEffect(() => { injectSelectStyles(); }, []);

  const handleClick = (newValue: string) => {
    if (onChange.length === 2) {
      (onChange as (e: React.SyntheticEvent, v: string) => void)({} as React.SyntheticEvent, newValue);
    } else {
      (onChange as (v: string) => void)(newValue);
    }
  };

  return (
    <div className="tm-scope tm-layout">
      <TabContext value={value}>
        {/* Tab bar — идентичный стиль с Timesheets */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'transparent',
          borderBottom: '1px solid rgba(var(--shodow), 0.15)',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'nowrap',
          flexShrink: 0,
          // Единый отступ под таб-баром для всего контента вкладок.
          // НЕ дублировать в потребителях — отступ принадлежит TabMenu.
          marginBottom: 'var(--space-l1)',
        }}>
          <div style={{ display: 'flex', flexShrink: 0, flexWrap: 'nowrap' }}>
            {tabs.filter(tab => !tab.hidden).map(tab => (
              <button
                key={tab.value}
                type="button"
                disabled={tab.disabled}
                onClick={() => !tab.disabled && handleClick(tab.value)}
                style={{
                  padding: '10px 18px',
                  fontSize: 12,
                  fontWeight: value === tab.value ? 800 : 600,
                  border: 'none',
                  background: 'transparent',
                  color: value === tab.value ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                  position: 'relative',
                  cursor: tab.disabled ? 'not-allowed' : 'pointer',
                  opacity: tab.disabled ? 0.4 : 1,
                  transition: 'color .15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
                {value === tab.value && (
                  <span style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 12,
                    right: 12,
                    height: 3,
                    background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                    borderRadius: '3px 3px 0 0',
                    display: 'block',
                  }} />
                )}
              </button>
            ))}
          </div>

          {rightElement && (
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', flexShrink: 0 }}>
              {rightElement}
            </div>
          )}
        </div>

        {children}
      </TabContext>
    </div>
  );
}
